import qz from 'qz-tray';

// QZ Tray connection state - singleton pattern
let isConnected = false;
let defaultPrinter = null;
let printerConfig = null;
let connectionPromise = null; // Prevent multiple simultaneous connection attempts

// Configure security ONCE at module load - this prevents repeated prompts
// Skip certificate validation for unsigned requests
qz.security.setCertificatePromise(function(resolve, reject) {
  resolve(); // Skip certificate check - only asked once per session
});

// Skip signature validation  
qz.security.setSignatureAlgorithm("SHA512");
qz.security.setSignaturePromise(function(toSign) {
  return function(resolve, reject) {
    resolve(); // Skip signature - only asked once per session
  };
});

// Thermal printer config - rasterize mode for reliable auto-cutter behavior
const THERMAL_CONFIG = {
  margins: { top: 0, right: 0, bottom: 0, left: 0 },
  units: 'mm',
  size: { width: 72 },   // Only set width - height auto-calculated from content
  colorType: 'blackwhite',
  copies: 2,
  rasterize: true,        // Render HTML to image first - prevents early cut
  scaleContent: false,    // Don't scale - print at native size
  altPrinting: false,     // Use default print pipeline for proper height calc
  interpolation: 'nearest-neighbor',  // Crisp text for thermal printer
  density: 8              // 8 dots/mm = ~203 DPI (standard thermal)
};

// Initialize QZ Tray connection - SINGLETON: only connects once per session
export const initQzTray = async () => {
  // Already connected - return immediately
  if (isConnected && qz.websocket.isActive()) {
    return true;
  }

  // Connection already in progress - wait for it
  if (connectionPromise) {
    return connectionPromise;
  }

  // Start new connection (only one "Allow" prompt will show)
  connectionPromise = (async () => {
    try {
      if (!qz.websocket.isActive()) {
        // Single connection attempt - this is the ONLY time user sees "Allow"
        await qz.websocket.connect({ retries: 2, delay: 1 });
      }
      isConnected = true;
      
      // Pre-load the default printer config
      const savedPrinter = localStorage.getItem('qz_default_printer');
      if (savedPrinter) {
        defaultPrinter = savedPrinter;
        printerConfig = qz.configs.create(savedPrinter, { ...THERMAL_CONFIG });
      }
      
      console.log('QZ Tray connected successfully');
      return true;
    } catch (error) {
      console.error('QZ Tray connection failed:', error);
      isConnected = false;
      connectionPromise = null; // Allow retry on failure
      return false;
    }
  })();

  return connectionPromise;
};

// Disconnect from QZ Tray
export const disconnectQzTray = async () => {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    isConnected = false;
    printerConfig = null;
  }
};

// Get list of available printers
export const getAvailablePrinters = async () => {
  try {
    if (!isConnected) {
      await initQzTray();
    }
    const printers = await qz.printers.find();
    return printers;
  } catch (error) {
    console.error('Failed to get printers:', error);
    return [];
  }
};

// Set default printer for receipts
export const setDefaultPrinter = (printerName) => {
  defaultPrinter = printerName;
  localStorage.setItem('qz_default_printer', printerName);
  printerConfig = qz.configs.create(printerName, { ...THERMAL_CONFIG });
};

// Get default printer
export const getDefaultPrinter = () => {
  if (!defaultPrinter) {
    defaultPrinter = localStorage.getItem('qz_default_printer');
  }
  return defaultPrinter;
};

// Print HTML receipt silently using QZ Tray - SINGLE CALL, NO EXTRA PROMPTS
export const printReceiptSilent = async (htmlContent, copies = 2, printerName = null) => {
  try {
    // Use the singleton connection - this ensures only ONE "Allow" prompt ever
    await initQzTray();
    
    if (!qz.websocket.isActive()) {
      throw new Error('QZ Tray not connected');
    }

    // Get printer to use
    const printer = printerName || getDefaultPrinter();
    if (!printer) {
      throw new Error('No printer selected');
    }

    // Create config for thermal receipt printing
    // Rasterize mode: QZ renders HTML to image, height = actual content height
    // Auto-cutter only fires after last pixel is printed
    const config = qz.configs.create(printer, {
      ...THERMAL_CONFIG,
      copies: copies
    });

    // Prepare HTML with print-optimized styles.
    // ---------------------------------------------------------------
    // Cutter-position primer:
    //   • The thermal auto-cutter sits 15-25 mm BELOW the print head,
    //     so paper has to advance at least that far after the last
    //     printed line or the cutter slices through the footer.
    //   • Chromium's PDF renderer decides where to split "pages" — if
    //     we tell it the page is fixed 5000 mm tall, a long receipt
    //     can still wrap because of `page-break-*` hints. When a wrap
    //     happens, the printer FIRES THE CUTTER between the two pages,
    //     producing exactly the "invoice split across two slips" bug
    //     the user has reported repeatedly.
    //   • Fix: `size: 72mm auto` → one page as tall as the content.
    //     Remove ALL page-break hints (there's only one page anyway).
    //     Bump the trailing feed zone to 45 mm to cover the worst-
    //     case cutter offset regardless of item count.
    const styledHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            @page {
              size: 72mm auto;
              margin: 0;
            }
            @media print {
              html, body {
                height: auto !important;
                overflow: visible !important;
              }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            html, body { break-inside: auto !important; page-break-inside: auto !important; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 14px;
              font-weight: 900;
              line-height: 1.4;
              width: 70mm;
              margin: 0 auto;
              padding: 1mm;
              color: #000;
              text-transform: uppercase;
            }
            img {
              width: 100%;
              max-width: 68mm;
              height: auto;
              display: block;
              margin: 0 auto 5mm auto;
            }
            .text-center { text-align: center; }
            div, span, p { font-weight: 900; color: #000; }
            /* Force the WHOLE receipt to render as a single unbroken
               page. Previous versions used
                 .totals-section { page-break-inside: avoid }
               which sounds safe, but in Chromium it means: "if the
               totals block wouldn't fully fit inside the CURRENT
               page-box, push it to a new page". With a fixed 5000 mm
               page-box that never fires, but with size:auto (which
               we now use) Chromium sizes the page-box just-so and any
               page-break hint can still trigger a split. We explicitly
               override to auto everywhere. */
            .receipt, .receipt *, .totals-section, .payment-info, .total-line {
              page-break-inside: auto !important;
              break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              break-before: auto !important;
              break-after: auto !important;
            }
          </style>
        </head>
        <body>${htmlContent}
          <!-- Bottom feed zone: 45 mm covers the worst-case thermal
               auto-cutter offset (15-25 mm typical, but some driver
               profiles add extra pre-cut feed). Blank space here is
               harmless — it's what actually gets sliced by the cutter,
               not the receipt content. -->
          <div style="height: 45mm;"></div>
        </body>
      </html>
    `;

    // Single print call - no additional prompts
    const data = [{ type: 'html', format: 'plain', data: styledHtml }];
    await qz.print(config, data);
    
    console.log(`Printed ${copies} copies to ${printer}`);
    return { success: true, printer: printer };

  } catch (error) {
    console.error('QZ Tray print error:', error);
    return { success: false, error: error.message };
  }
};

// Check if QZ Tray is available
export const isQzTrayAvailable = async () => {
  try {
    if (qz.websocket.isActive()) {
      return true;
    }
    await initQzTray();
    return isConnected;
  } catch {
    return false;
  }
};

// Get connection status
export const getConnectionStatus = () => {
  return {
    connected: isConnected && qz.websocket.isActive(),
    defaultPrinter: getDefaultPrinter()
  };
};
